import { gql } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';
import AppError from '../../../../app/features/error/services/AppError';
import useGraphQLClient from '../../../../app/hooks/useGraphQLClient';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import _ from 'lodash';
import IInvestExpertiseFiltersResult from '../../interfaces/expertise/IInvestExpertiseFiltersResult';
import { useAppNavigationContext } from '../../../../app/features/navigation/providers/AppNavigationProvider';
import { TInvestFundCurrency } from '../../types/fund/TInvestFundCurrency';

type useInvestExpertiseKpisProps = {
	typeTag: string | null;
	fundIds: number[] | null;
	investorIds: number[] | null;
	reportingPeriod: string | null;
};

const useInvestExpertiseKpis = (filters: useInvestExpertiseKpisProps) => {
	const auth = useAuthContext();
	const graphQLClient = useGraphQLClient();

	const navigationContext = useAppNavigationContext();

	const filter: useInvestExpertiseKpisProps & { currency?: TInvestFundCurrency } = {
		typeTag: filters.typeTag,
		fundIds: filters.fundIds,
		investorIds: filters.investorIds,
		reportingPeriod: filters.reportingPeriod,
	};

	// Currency can be mixed for venture_capital, in this case we remove currency filter for VC
	if (filters.typeTag !== 'venture_capital') {
		filter.currency = navigationContext.currentCurrency;
	}
	const { data, isFetching } = useQuery<IInvestExpertiseFiltersResult, Error>(
		['investmentTypeDashboard', filter],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query (
							$typeTag: String
							$fundIds: [Int]
							$investorIds: [Int]
							$reportingPeriod: String
							$currency: String
						) {
							investmentTypeDashboard(
								typeTag: $typeTag
								fundIds: $fundIds
								investorIds: $investorIds
								reportingPeriod: $reportingPeriod
								currency: $currency
							) {
								dataUpdatedOn
								dataUpdatedOnSg
								isEstimated
								kpis {
									currency
									arr
									arrIsNs
									irr
									irrIsNs
									irrGross
									irrGrossIsN
									fees
									committedCapital
									calledCapital
									callableCapital
									netCalledCapital
									tvpi
									tvpiIsNs
									dpi
									dpiIsNs
									rvpi
									rvpiIsNs
									distributedInterestIncome
									distributedReturnOfCapital
									netAssetValue
								}
								kpisByFund {
									fund {
										id
										name
										creationDate
										isCoinvestment
										currency
									}
									kpis {
										currency
										calledCapital
										committedCapital
										irr
										irrIsNs
										irrGross
										irrGrossIsN
										fees
										tvpi
										dpi
										rvpi
										netAssetValue
										totalDistributed
										distributionsRecallable
										totalValue
									}
								}
								kpisByFundByInvestorByAsset {
									fund {
										id
										name
										isCoinvestment
										currency
									}
									investor {
										id
										name
										group {
											id
											name
										}
									}
									asset {
										name
										imgSrc
										currency
										sectorNameText
										countryCode
										investment {
											date
											multiple
											reportingStatus
											reportingFMV
										}
									}
									kpis {
										currency
										committedCapital
										calledCapital
										totalValue
										totalDistributed
									}
								}
								portfolioFunds {
									investor {
										id
										name
										group {
											id
											name
										}
									}
									fund {
										id
										name
										currency
									}
									fundStatus
									investmentYear
									reportingPeriod {
										tag
										name
									}
									reportingDate
									stats {
										currency
										committedCapital
										percentOwned
										calledCapital
										callableCapital
										totalValue
										totalDistributed
										interestIncome
										returnOfCapital
										netCalledCapital
										netAssetValue
										irr
										irrIsNs
										arr
										arrIsNs
										dpi
										rvpi
									}
								}
								kpisByFundForSiennaGestion {
									fund {
										id
										name
										creationDate
										isCoinvestment
										currency
									}
									kpi {
										aum
										navPerShare
										synthesisCumulativePerformance {
											dateType
											value
											dateCalcul
										}
									}
									assetsFundList {
										valuationDate
										assetsFund {
											name
											country
											businessSector
											assetType
											weightInFund
										}
									}
								}
							}
						}
					`,
					filter,
				)) as { investmentTypeDashboard: IInvestExpertiseFiltersResult };
				const dataTmp = _.cloneDeep(data.investmentTypeDashboard);

				// TODO(technical debt): Find viable solution to remove 'MH CROISSANCE HAUT-DE-FRANCE - PART A' perf from data
				// Put all reporting period data to null for funds named 'MH CROISSANCE HAUT-DE-FRANCE - PART A'
				if (dataTmp.portfolioFunds && dataTmp.kpisByFundForSiennaGestion) {
					dataTmp.kpisByFundForSiennaGestion.forEach((fund) => {
						if (fund.fund?.name === 'MH CROISSANCE HAUT-DE-FRANCE - PART A') {
							fund.kpi?.synthesisCumulativePerformance?.forEach((perf) => {
								perf.value = null;
							});
						}
					});
				}
				return dataTmp;
			} catch (e) {
				throw new AppError(e, 'useInvestExpertiseKpis');
			}
		},
		{
			enabled:
				auth.isInvestor === true &&
				auth.currentCompany !== null &&
				auth.isAuthenticated &&
				filter.fundIds !== null &&
				filters.typeTag !== null &&
				navigationContext.currentCurrency !== null,
		},
	);
	return {
		isFetching,
		results: data ?? null,
	};
};

export default useInvestExpertiseKpis;
